import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Plus, Eye, EyeOff, ExternalLink, LogOut, Copy, Trash2, QrCode, Download, X, Pencil, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function QRModal({ slug, lovedOneName, onClose }) {
  const qrRef = useRef(null);
  const url = `${window.location.origin}/heirloom/${slug}`;

  const downloadQR = useCallback(() => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 800;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#FCFBF9';
      ctx.fillRect(0, 0, 800, 800);
      ctx.drawImage(img, 100, 100, 600, 600);
      const a = document.createElement('a');
      a.download = `${slug}-qr-code.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }, [slug]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 animate-fade-in" onClick={onClose} data-testid="qr-modal">
      <div className="bg-[#FCFBF9] max-w-sm w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#78716C] hover:text-[#1C1917]" data-testid="qr-modal-close"><X size={20} /></button>
        <div className="text-center">
          <p className="overline mb-2">QR Code</p>
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#1C1917] mb-6">{lovedOneName}</h3>
          <div ref={qrRef} className="flex justify-center mb-6">
            <QRCodeSVG
              value={url}
              size={220}
              level="H"
              fgColor="#1C1917"
              bgColor="#FCFBF9"
              data-testid="qr-code-svg"
            />
          </div>
          <p className="text-xs text-[#78716C] mb-6 break-all">{url}</p>
          <div className="flex gap-3">
            <button
              onClick={() => { navigator.clipboard.writeText(url); toast.success('Link copied!'); }}
              data-testid="qr-copy-link"
              className="flex-1 flex items-center justify-center gap-2 border border-[#D6D3CD] hover:bg-[#F2EFE9] py-2.5 text-sm text-[#1C1917] transition-colors"
            >
              <Copy size={14} /> Copy Link
            </button>
            <button
              onClick={downloadQR}
              data-testid="qr-download"
              className="flex-1 flex items-center justify-center gap-2 bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] py-2.5 text-sm transition-colors"
            >
              <Download size={14} /> Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  contacted: 'bg-amber-50 text-amber-700 border-amber-200',
  closed: 'bg-[#F2EFE9] text-[#78716C] border-[#E7E5DF]',
};

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  const [qrProject, setQrProject] = useState(null);
  const [newMarket, setNewMarket] = useState('');
  const [editingMarket, setEditingMarket] = useState(null);
  const [calendlyUrl, setCalendlyUrl] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate('/admin/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };
  };

  const fetchData = async () => {
    try {
      const [projRes, inqRes, mktRes] = await Promise.all([
        axios.get(`${API}/projects`, getAuthHeaders()),
        axios.get(`${API}/inquiries`, getAuthHeaders()),
        axios.get(`${API}/markets/all`, getAuthHeaders()),
      ]);
      setProjects(projRes.data);
      setInquiries(inqRes.data);
      setMarkets(mktRes.data);
      try {
        const settingsRes = await axios.get(`${API}/settings`, getAuthHeaders());
        setCalendlyUrl(settingsRes.data.calendly_url || '');
      } catch {}
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const togglePublish = async (project) => {
    try {
      await axios.put(`${API}/projects/${project.id}`, { is_published: !project.is_published }, getAuthHeaders());
      fetchData();
      toast.success(project.is_published ? 'Unpublished' : 'Published');
    } catch {
      toast.error('Failed to update');
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/projects/${id}`, getAuthHeaders());
      fetchData();
      toast.success('Project deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await axios.put(`${API}/inquiries/${id}/status`, { status }, getAuthHeaders());
      fetchData();
      toast.success(`Status updated to ${status}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#A89F91] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="admin-dashboard">
      <header className="border-b border-[#E7E5DF] bg-[#FCFBF9] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between h-16">
          <h1 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1C1917]">
            Heirloom Stories <span className="text-[#78716C] font-normal">Admin</span>
          </h1>
          <button onClick={() => { logout(); navigate('/admin/login'); }} data-testid="admin-logout-button" className="flex items-center gap-2 text-sm text-[#78716C] hover:text-[#1C1917] transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-8 sm:py-12">
        <div className="flex gap-6 mb-8 border-b border-[#E7E5DF]">
          <button
            onClick={() => setActiveTab('projects')}
            data-testid="tab-projects"
            className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'projects' ? 'text-[#1C1917] border-b-2 border-[#1C1917]' : 'text-[#78716C] hover:text-[#57534E]'}`}
          >
            Heirloom Projects ({projects.length})
          </button>
          <button
            onClick={() => setActiveTab('inquiries')}
            data-testid="tab-inquiries"
            className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'inquiries' ? 'text-[#1C1917] border-b-2 border-[#1C1917]' : 'text-[#78716C] hover:text-[#57534E]'}`}
          >
            Inquiries ({inquiries.length})
          </button>
          <button
            onClick={() => setActiveTab('markets')}
            data-testid="tab-markets"
            className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'markets' ? 'text-[#1C1917] border-b-2 border-[#1C1917]' : 'text-[#78716C] hover:text-[#57534E]'}`}
          >
            Markets ({markets.length})
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            data-testid="tab-settings"
            className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'settings' ? 'text-[#1C1917] border-b-2 border-[#1C1917]' : 'text-[#78716C] hover:text-[#57534E]'}`}
          >
            Settings
          </button>
        </div>

        {activeTab === 'projects' && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium text-[#1C1917]">Projects</h2>
              <Link
                to="/admin/projects/new"
                data-testid="create-project-button"
                className="inline-flex items-center gap-2 bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] px-5 py-2.5 text-sm font-medium tracking-wide transition-all duration-300"
              >
                <Plus size={16} /> New Project
              </Link>
            </div>
            {projects.length === 0 ? (
              <div className="text-center py-20 text-[#78716C]">
                <p className="text-lg mb-2">No projects yet</p>
                <p className="text-sm">Create your first heirloom project to get started.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {projects.map((p) => (
                  <div key={p.id} data-testid={`project-row-${p.id}`} className="border border-[#E7E5DF] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 hover:border-[#D6D3CD] transition-colors">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#1C1917] mb-1">{p.loved_one_name}</h3>
                      <p className="text-sm text-[#78716C]">/{p.slug} &middot; {p.access_type} &middot; {new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 shrink-0 flex-wrap">
                      <span className={`text-xs font-semibold px-3 py-1 ${p.is_published ? 'bg-green-50 text-green-700' : 'bg-[#F2EFE9] text-[#78716C]'}`}>
                        {p.is_published ? 'Published' : 'Draft'}
                      </span>
                      <button onClick={() => togglePublish(p)} data-testid={`toggle-publish-${p.id}`} className="p-2 text-[#78716C] hover:text-[#1C1917] transition-colors" title={p.is_published ? 'Unpublish' : 'Publish'}>
                        {p.is_published ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button onClick={() => setQrProject(p)} data-testid={`qr-button-${p.id}`} className="p-2 text-[#78716C] hover:text-[#1C1917] transition-colors" title="QR Code">
                        <QrCode size={16} />
                      </button>
                      <Link to={`/admin/preview/${p.id}`} target="_blank" data-testid={`preview-link-${p.id}`} className="p-2 text-[#78716C] hover:text-[#1C1917] transition-colors" title="Preview">
                        <ExternalLink size={16} />
                      </Link>
                      <Link to={`/admin/projects/${p.id}`} data-testid={`edit-project-${p.id}`} className="text-sm text-[#1C1917] border border-[#D6D3CD] hover:bg-[#F2EFE9] px-4 py-1.5 transition-colors">
                        Edit
                      </Link>
                      <button onClick={() => deleteProject(p.id)} data-testid={`delete-project-${p.id}`} className="p-2 text-red-400 hover:text-red-600 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'inquiries' && (
          <>
            <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium text-[#1C1917] mb-8">Inquiries</h2>
            {inquiries.length === 0 ? (
              <div className="text-center py-20 text-[#78716C]">
                <p className="text-lg">No inquiries yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inq) => (
                  <div key={inq.id} data-testid={`inquiry-row-${inq.id}`} className="border border-[#E7E5DF] p-5 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-[#1C1917]">{inq.name}</h3>
                        <p className="text-sm text-[#78716C]">{inq.email}{inq.phone ? ` · ${inq.phone}` : ''}</p>
                      </div>
                      <select
                        value={inq.status || 'new'}
                        onChange={(e) => updateInquiryStatus(inq.id, e.target.value)}
                        data-testid={`inquiry-status-${inq.id}`}
                        className={`text-xs font-semibold px-3 py-1.5 border outline-none cursor-pointer ${STATUS_COLORS[inq.status || 'new']}`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    <p className="text-sm text-[#57534E]">
                      <span className="font-medium">Loved one:</span> {inq.loved_one_name} ({inq.relationship})
                      {inq.market && <span className="ml-3 text-[#78716C]"><MapPin size={12} className="inline -mt-0.5" /> {inq.market}</span>}
                    </p>
                    {inq.notes && <p className="text-sm text-[#78716C] mt-2">{inq.notes}</p>}
                    <p className="text-xs text-[#A89F91] mt-3">{new Date(inq.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {activeTab === 'settings' && (
        <div className="max-w-7xl mx-auto px-6 sm:px-12 pb-12">
          <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium text-[#1C1917] mb-8">Settings</h2>
          <div className="border border-[#E7E5DF] p-6 sm:p-8 max-w-xl" data-testid="calendly-settings">
            <h3 className="text-sm font-semibold text-[#1C1917] mb-1">Scheduling Link (Calendly)</h3>
            <p className="text-xs text-[#78716C] mb-4">Add your Calendly URL to embed scheduling on the inquiry confirmation page.</p>
            <div className="flex items-center gap-3">
              <input
                value={calendlyUrl}
                onChange={(e) => setCalendlyUrl(e.target.value)}
                data-testid="calendly-url-input"
                className="flex-1 border border-[#D6D3CD] focus:border-[#1C1917] px-4 py-2.5 text-sm text-[#1C1917] outline-none bg-transparent"
                placeholder="https://calendly.com/your-name/consultation"
              />
              <button
                disabled={savingSettings}
                onClick={async () => {
                  setSavingSettings(true);
                  try {
                    await axios.put(`${API}/settings`, { calendly_url: calendlyUrl }, getAuthHeaders());
                    toast.success('Settings saved');
                  } catch { toast.error('Failed to save'); }
                  finally { setSavingSettings(false); }
                }}
                data-testid="save-calendly-button"
                className="bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] disabled:opacity-50 px-5 py-2.5 text-sm font-medium transition-colors"
              >
                {savingSettings ? 'Saving...' : 'Save'}
              </button>
            </div>
            {calendlyUrl && <p className="text-xs text-green-600 mt-2">Active — scheduling will appear on confirmation page</p>}
          </div>
        </div>
      )}

      {qrProject && (
        <QRModal
          slug={qrProject.slug}
          lovedOneName={qrProject.loved_one_name}
          onClose={() => setQrProject(null)}
        />
      )}

      {activeTab === 'markets' && (
        <div className="max-w-7xl mx-auto px-6 sm:px-12 pb-12">
          <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium text-[#1C1917] mb-8">Service Markets</h2>
          <div className="flex items-center gap-3 mb-8" data-testid="add-market-form">
            <input
              value={newMarket}
              onChange={(e) => setNewMarket(e.target.value)}
              placeholder="e.g., Nashville, TN"
              data-testid="new-market-input"
              className="flex-1 border border-[#D6D3CD] focus:border-[#1C1917] px-4 py-2.5 text-sm text-[#1C1917] outline-none bg-transparent"
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && newMarket.trim()) {
                  try {
                    await axios.post(`${API}/markets`, { name: newMarket.trim() }, getAuthHeaders());
                    setNewMarket('');
                    fetchData();
                    toast.success('Market added');
                  } catch { toast.error('Failed to add market'); }
                }
              }}
            />
            <button
              onClick={async () => {
                if (!newMarket.trim()) return;
                try {
                  await axios.post(`${API}/markets`, { name: newMarket.trim() }, getAuthHeaders());
                  setNewMarket('');
                  fetchData();
                  toast.success('Market added');
                } catch { toast.error('Failed to add market'); }
              }}
              data-testid="add-market-button"
              className="bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] px-5 py-2.5 text-sm font-medium transition-colors"
            >
              Add Market
            </button>
          </div>
          <div className="space-y-3">
            {markets.map(m => (
              <div key={m.id} data-testid={`market-row-${m.id}`} className="border border-[#E7E5DF] p-4 flex items-center gap-4 hover:border-[#D6D3CD] transition-colors">
                {editingMarket === m.id ? (
                  <input
                    defaultValue={m.name}
                    autoFocus
                    className="flex-1 border border-[#D6D3CD] focus:border-[#1C1917] px-3 py-1.5 text-sm outline-none bg-transparent"
                    onBlur={async (e) => {
                      const val = e.target.value.trim();
                      if (val && val !== m.name) {
                        await axios.put(`${API}/markets/${m.id}`, { name: val }, getAuthHeaders());
                        fetchData();
                        toast.success('Market updated');
                      }
                      setEditingMarket(null);
                    }}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') e.target.blur();
                      if (e.key === 'Escape') setEditingMarket(null);
                    }}
                  />
                ) : (
                  <span className="flex-1 text-sm text-[#1C1917]">{m.name}</span>
                )}
                <span className={`text-xs px-2 py-0.5 ${m.is_active ? 'bg-green-50 text-green-700' : 'bg-[#F2EFE9] text-[#78716C]'}`}>
                  {m.is_active ? 'Active' : 'Inactive'}
                </span>
                <button onClick={async () => {
                  await axios.put(`${API}/markets/${m.id}`, { is_active: !m.is_active }, getAuthHeaders());
                  fetchData();
                  toast.success(m.is_active ? 'Deactivated' : 'Activated');
                }} className="text-xs text-[#78716C] hover:text-[#1C1917] border border-[#D6D3CD] px-3 py-1 transition-colors" data-testid={`toggle-market-${m.id}`}>
                  {m.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={() => setEditingMarket(m.id)} className="p-1.5 text-[#78716C] hover:text-[#1C1917] transition-colors"><Pencil size={14} /></button>
                <button onClick={async () => {
                  if (!window.confirm(`Remove "${m.name}"?`)) return;
                  await axios.delete(`${API}/markets/${m.id}`, getAuthHeaders());
                  fetchData();
                  toast.success('Market removed');
                }} className="p-1.5 text-red-400 hover:text-red-600 transition-colors" data-testid={`delete-market-${m.id}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
